export type PostHogErrorProperties = Record<
  string,
  boolean | number | string | undefined
>;

type PostHogCookiePayload = {
  distinct_id?: unknown;
};

type PostHogEventLike = {
  event?: string;
  properties?: Record<string, unknown>;
};

const POSTHOG_COOKIE_NAME_PATTERN = /^ph_phc_.*_posthog$/;
const POSTHOG_EXCEPTION_EVENT = '$exception';
const REDACTED_VALUE = '[REDACTED]';
const SENSITIVE_PROPERTY_KEYS = new Set([
  'accesstoken',
  'apikey',
  'authorization',
  'body',
  'chatmessage',
  'chatmessages',
  'completion',
  'completiontext',
  'content',
  'cookie',
  'documenttext',
  'extractedtext',
  'filecontent',
  'filecontents',
  'filename',
  'filepath',
  'headers',
  'llmprompt',
  'llmresponse',
  'message',
  'messages',
  'ocrtext',
  'password',
  'prompt',
  'prompts',
  'refreshtoken',
  'requestbody',
  'response',
  'responsebody',
  'responses',
  'secret',
  'setcookie',
  'text',
  'token',
]);

function normalizePropertyKey(key: string): string {
  return key.toLowerCase().replaceAll(/[^a-z0-9]/g, '');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isSensitivePropertyKey(key: string): boolean {
  const normalizedKey = normalizePropertyKey(key);
  if (key.startsWith('$exception_')) return false;

  return (
    SENSITIVE_PROPERTY_KEYS.has(normalizedKey) ||
    normalizedKey.endsWith('token') ||
    normalizedKey.endsWith('secret')
  );
}

function isUrlPropertyKey(key: string): boolean {
  const normalizedKey = normalizePropertyKey(key);
  return (
    normalizedKey.includes('url') ||
    normalizedKey === 'currenturl' ||
    normalizedKey === 'referrer'
  );
}

export function stripQueryAndHash(value: string): string {
  try {
    const url = new URL(value);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split(/[?#]/, 1)[0] || value;
  }
}

function sanitizeUrlPropertyValue(value: unknown): unknown {
  if (typeof value === 'string') return stripQueryAndHash(value);

  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === 'string' ? stripQueryAndHash(item) : item,
    );
  }

  return value;
}

export function getPostHogExceptionProperties(
  properties: PostHogErrorProperties,
): PostHogErrorProperties {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  ) as PostHogErrorProperties;
}

export function getPostHogDeploymentProperties(): PostHogErrorProperties {
  const vercelUrl =
    process.env.VERCEL_URL ?? process.env.NEXT_PUBLIC_VERCEL_URL;
  const vercelDeploymentUrl = vercelUrl
    ? `https://${vercelUrl.replace(/^https?:\/\//, '')}`
    : undefined;

  return getPostHogExceptionProperties({
    vercel_deployment_url: vercelDeploymentUrl,
    vercel_env: process.env.VERCEL_ENV ?? process.env.NEXT_PUBLIC_VERCEL_ENV,
    vercel_target_env:
      process.env.VERCEL_TARGET_ENV ??
      process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV,
  });
}

export function redactPostHogExceptionEvent<T extends PostHogEventLike | null>(
  event: T,
  metadata: PostHogErrorProperties = {},
): T {
  if (!event || event.event !== POSTHOG_EXCEPTION_EVENT) return event;

  const eventProperties = isRecord(event.properties) ? event.properties : {};
  const redactedProperties: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(eventProperties)) {
    if (key === '$exception_steps') {
      redactedProperties.exception_steps_redacted = true;
      continue;
    }

    if (isSensitivePropertyKey(key)) {
      redactedProperties[key] = REDACTED_VALUE;
      continue;
    }

    redactedProperties[key] = isUrlPropertyKey(key)
      ? sanitizeUrlPropertyValue(value)
      : value;
  }

  return {
    ...event,
    properties: {
      ...redactedProperties,
      ...getPostHogExceptionProperties(metadata),
    },
  } as T;
}

/**
 * Extract the PostHog browser distinct_id from cookie headers without exposing
 * other cookie values to observability providers.
 */
export function getPostHogDistinctIdFromCookieHeader(
  cookieHeader: string | string[] | null | undefined,
): string | undefined {
  if (!cookieHeader) return undefined;

  const normalizedCookieHeader = Array.isArray(cookieHeader)
    ? cookieHeader.join('; ')
    : cookieHeader;

  for (const cookiePair of normalizedCookieHeader.split(';')) {
    const [rawName, ...rawValueParts] = cookiePair.trim().split('=');
    if (!rawName || rawValueParts.length === 0) continue;
    if (!POSTHOG_COOKIE_NAME_PATTERN.test(rawName)) continue;

    try {
      const payload = JSON.parse(
        decodeURIComponent(rawValueParts.join('=')),
      ) as PostHogCookiePayload;
      return typeof payload.distinct_id === 'string'
        ? payload.distinct_id
        : undefined;
    } catch {
      return undefined;
    }
  }

  return undefined;
}
